import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup 
} from 'react-bootstrap';
import { 
  FaEdit, FaTrash, FaPlus, FaSearch, 
  FaCar, FaUsers, FaSuitcase 
} from 'react-icons/fa';
import axios from 'axios';
import { Link } from 'react-router-dom';

const VehicleList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchVehicles = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/vehicles`, {
        headers: { 'x-auth-token': token },
        params: { 
          page,
          search: searchTerm 
        }
      });
      
      setVehicles(response.data.vehicles);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setError(err.response?.data?.error || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, [searchTerm]);

  const handleDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(`${process.env.REACT_APP_API_URL}/admin/vehicles/${vehicleToDelete._id}`, {
        headers: { 'x-auth-token': token }
      });
      
      setShowDeleteModal(false);
      fetchVehicles(currentPage);
    } catch (err) {
      console.error('Error deleting vehicle:', err);
      setError(err.response?.data?.error || 'Failed to delete vehicle');
    }
  };

  const getCategoryBadge = (category) => {
    const variants = {
      'Luxury': 'primary',
      'Premium': 'success',
      'Extra Premium': 'warning'
    };
    return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
  };

  return (
    <div className="vehicles-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Vehicle Management</h2>
        <Link to="/admin/vehicles/add" className="btn btn-primary">
          <FaPlus className="me-2" />
          Add New Vehicle
        </Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search vehicles by name or category"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Vehicle</th>
                <th>Category</th>
                <th>Capacity</th>
                <th>Base Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.length > 0 ? (
                vehicles.map((vehicle) => (
                  <tr key={vehicle._id}>
                    <td>{vehicle.Vehicle_ID}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaCar className="me-2" />
                        <div>
                          <div>{vehicle.Vehicle_Name}</div>
                          <small className="text-muted">Per km: ${vehicle.Price_Per_Km}</small>
                        </div>
                      </div>
                    </td>
                    <td>{getCategoryBadge(vehicle.Vehicle_Category)}</td>
                    <td>
                      <div>
                        <div><FaUsers className="me-2" /> {vehicle.Seating_Capacity} seats</div>
                        <div><FaSuitcase className="me-2" /> {vehicle.Luggage} luggage</div>
                      </div>
                    </td>
                    <td>${vehicle.Basic_Price} (first {vehicle.Basic_Distance}km)</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Link 
                          to={`/admin/vehicles/edit/${vehicle._id}`}
                          className="btn btn-sm btn-primary"
                        >
                          <FaEdit />
                        </Link>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setVehicleToDelete(vehicle);
                            setShowDeleteModal(true);
                          }}
                        >
                          <FaTrash />
                        </Button>
                        <Link
                          to={`/admin/vehicles/pricing/${vehicle._id}`}
                          className="btn btn-sm btn-info"
                        >
                          Pricing
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No vehicles found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchVehicles(currentPage - 1)}
                    >
                      Previous
                    </button>
                  </li>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <li 
                      key={page} 
                      className={`page-item ${currentPage === page ? 'active' : ''}`}
                    >
                      <button 
                        className="page-link" 
                        onClick={() => fetchVehicles(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => fetchVehicles(currentPage + 1)}
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete vehicle <strong>{vehicleToDelete?.Vehicle_Name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete Vehicle
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VehicleList;